'use client';

import { useReveal } from '@/hooks/use-reveal';

export default function Heritage() {
  const { ref, visible } = useReveal();

  return (
    <section id="heritage" ref={ref} className="relative py-24 md:py-36 bg-[#2B1810] overflow-hidden">
      {/* Background texture */}
      <div className="absolute inset-0 bg-leather-grain opacity-90" />
      <div
        className="absolute inset-0 opacity-30 mix-blend-soft-light"
        style={{
          backgroundImage:
            'radial-gradient(circle at 30% 20%, rgba(212,160,23,0.2) 0%, transparent 60%)',
        }}
      />

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          {/* Left — Image */}
          <div className={`reveal ${visible ? 'visible' : ''} relative`}>
            <div className="relative aspect-[4/5] overflow-hidden rounded-sm group">
              <img
                src="/images/heritage-artisan.png"
                alt="Artisan couseur de cuir ASMAO STYLE CUIR"
                className="w-full h-full object-cover transition-transform duration-[2s] group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#2B1810]/60 via-transparent to-transparent" />
              {/* Frame border */}
              <div className="absolute inset-4 border border-[#C9A961]/40 pointer-events-none" />
            </div>

            {/* Floating badge */}
            <div className="absolute -bottom-8 -right-4 md:-right-8 bg-[#C9A961] text-[#2B1810] p-6 md:p-8 rounded-sm shadow-2xl max-w-[200px]">
              <div className="font-display text-4xl md:text-5xl font-bold leading-none">25</div>
              <div className="text-xs tracking-[0.25em] uppercase mt-2 font-medium">
                Années
                <br />
                de Passion
              </div>
            </div>
          </div>

          {/* Right — Text */}
          <div className="text-[#F8F1E4]">
            <div className={`reveal ${visible ? 'visible' : ''} mb-6`}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-px bg-[#C9A961]" />
                <span className="text-[#C9A961] font-serif-display text-sm tracking-[0.4em] uppercase">
                  Notre Héritage
                </span>
              </div>
            </div>

            <h2 className={`reveal ${visible ? 'visible' : ''} font-display text-4xl md:text-5xl lg:text-6xl font-medium leading-[1.1] mb-8`}>
              Le Geste qui traverse
              <br />
              <span className="italic text-luxe-gold">les Générations</span>
            </h2>

            <div className={`reveal ${visible ? 'visible' : ''} space-y-6 text-[#F8F1E4]/85 font-serif-display font-light text-lg leading-relaxed`}>
              <p>
                L'histoire d'ASMAO STYLE CUIR est celle d'une famille animée par une conviction
                simple : le cuir est une matière vivante qui mérite d'être traitée avec respect,
                patience et amour. Transmise de génération en génération, cette philosophie
                trouve aujourd'hui son écrin au cœur du 18e arrondissement de Paris.
              </p>
              <p>
                Nos artisans ont appris le métier auprès de maîtres selliers, dans la tradition
                des plus grandes maisons françaises. Ils connaissent chaque cuir, chaque fibre,
                chaque nuance. Ils savent quand la main doit presser, quand elle doit attendre,
                quand elle doit lâcher. Ce savoir ne s'apprend pas dans les livres — il se transmet
                par l'observation, la répétition, l'humilité.
              </p>
              <p className="text-[#C9A961] font-medium italic">
                « Un beau cuir ne se porte pas. Il se vit, il vieillit, il raconte. »
              </p>
            </div>

            {/* Values row */}
            <div className={`reveal ${visible ? 'visible' : ''} mt-12 grid grid-cols-3 gap-6`}>
              {[
                { num: '01', label: 'Cuir Pleine Fleur' },
                { num: '02', label: 'Couture Sellier' },
                { num: '03', label: 'Tannage Végétal' },
              ].map((v) => (
                <div key={v.num} className="border-t border-[#C9A961]/30 pt-4">
                  <div className="text-[#C9A961] font-display text-2xl mb-2">{v.num}</div>
                  <div className="text-[#F8F1E4]/70 text-xs tracking-[0.2em] uppercase font-serif-display">
                    {v.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
